import type { ComponentPropsWithoutRef } from 'react'
import { useClientEnv } from '~/utils/client/hooks/useClientEnv'

export type ApiImageSet = (string | null)[] | null

export enum ApiImageSize {
  Small = 0,
  Medium = 1,
  Large = 2,
}

export type ApiImageProps = {
  imageSet?: ApiImageSet
  minimumSize?: ApiImageSize
} & Omit<ComponentPropsWithoutRef<'img'>, 'src'>

const getImageFromSet = ({
  imageSet,
  minimumSize = ApiImageSize.Small,
}: ApiImageProps): string | undefined | null => {
  if (!imageSet || imageSet.length < 1) return undefined

  // tries to find the preferred size or, if not present, the next bigger one
  for (let i = minimumSize; i < imageSet.length; i++) {
    if (imageSet[i]) return imageSet[i]
  }

  // default first entry
  return imageSet[0]
}

export const ApiImage = ({ imageSet, minimumSize, alt, ...props }: ApiImageProps) => {
  const clientEnv = useClientEnv()
  const source =
    getImageFromSet({ imageSet, minimumSize }) ||
    `${clientEnv.publicPath}/images/placeholder-image.png`

  // nullish images return empty string and are filtered out afterwards
  const srcSet = imageSet
    ?.map((img, index) => (img ? `${img} ${index * 500 + 500}w` : ''))
    .filter((entry) => entry !== '')
    .join(', ')

  return <img src={source} alt={alt} loading="lazy" srcSet={srcSet} {...props} />
}
